import React, { useState } from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import {
  loadImageDimensions,
  urlToBase64,
  capitalizeFirstLetter,
  joinAddress,
  parseAmount,
} from "../modules/invoiceUtils";
import uRLs from "../../utils/baseUtils";
import { usePermission } from "../../context/PermissionContext";

export const generateInvComInvoicePdf = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const {
    documentConfig,
    isInterState,
    isPreSale,
    hasPayment
    
  } = invoiceConfig;
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  // console.log("storeSettings?.negobillBranding",storeSettings?.negobillBranding)
  // Header Logic
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const COL_RIGHT = pageW - 65;
  const COL_MIDDLE = pageW / 2.8;
  let y = PADDING + 5;

  const invoiceText =
    data?.status === "CANCELLED" ? "CANCELLED" : documentConfig?.title;
  const originalText = "ORIGINAL FOR RECIPIENT";
  function drawSpacedText(doc, text, x, y, spacing = 0.8) {
    let cursorX = x;
    for (const ch of text) {
      doc.text(ch, cursorX, y);
      cursorX += doc.getTextWidth(ch) + spacing;
    }
  }
  // --- Row 1: Header Titles ---
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(14);
  doc.setTextColor(20, 40, 100); // Navy blue for "TAX INVOICE"
  // doc.text(invoiceText, MARGIN_LEFT, y);
  drawSpacedText(doc, invoiceText, MARGIN_LEFT, y, 0.6);

  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90); // Gray
  // doc.text(originalText, MARGIN_RIGHT, y, { align: "right" });
  const textWidth = originalText
    .split("")
    .reduce((w, ch) => w + doc.getTextWidth(ch) + 0.6, 0);

  drawSpacedText(doc, originalText, MARGIN_RIGHT - textWidth, y, 0.6);

  const address = joinAddress(
    documentConfig?.fromData?.addressline1,
    documentConfig?.fromData?.addressline2,
    documentConfig?.fromData?.district,
    documentConfig?.fromData?.state,
    documentConfig?.fromData?.pincode,
  );

  // --- Row 2: Seller Info & Logo ---
  y = 20;
  doc.setFontSize(13);
  doc.setTextColor(0);
  doc.text(documentConfig?.fromData?.title || "", MARGIN_LEFT, y);

  // Placeholder for Logo (Replace with doc.addImage(base64, 'PNG', x, y, w, h))
  doc.setFontSize(22);
  doc.setTextColor(34, 139, 34); // DMart Green
  if (documentConfig?.image) {
    try {
      const imagePath =
         documentConfig?.image;

      // const imagePath = settings?.saleProfileData?.image ?  settings?.saleProfileData?.image : "/assets/images/logo-light.png";
      const base64 = await urlToBase64(imagePath);
      const { width, height } = await loadImageDimensions(base64);

      const maxWidth = 40;
      const maxHeight = 10;

      let imgW = width;
      let imgH = height;

      const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);

      const finalW = imgW * ratio;
      const finalH = imgH * ratio;

      const ext = imagePath.split(".").pop().toLowerCase();
      const format = ext === "png" ? "PNG" : "JPEG";
      const logoX = MARGIN_RIGHT - finalW;
      // Use finalW and finalH instead of maxWidth and maxHeight
      doc.addImage(base64, format, logoX, y, finalW, finalH);
      // doc.addImage(base64, format, pageW - 60, y - 5, maxWidth, maxHeight);
    } catch (err) {
      console.warn("Logo load failed:", err);
      // Fallback text if logo fails
      doc.setFontSize(22);
      doc.setTextColor(34, 139, 34);
      doc.text("Test-Store", MARGIN_RIGHT, y + 5, { align: "right" });
    }
  }

  doc.setFontSize(9);
  doc.setFont("Inter-Bold", "bold");
  doc.setTextColor(0);
  y += 6;
  doc.text(
    `GSTIN: ${documentConfig?.fromData?.gstnumber || ""}`,
    MARGIN_LEFT,
    y,
  );
  y += 4;
  doc.setFont("Inter-Regular", "normal");
  const addressWidth = pageW / 2 - MARGIN_LEFT - 5;
  const addressLines = doc.splitTextToSize(address, addressWidth);
  doc.text(addressLines, MARGIN_LEFT, y);
  y += addressLines.length * 4;

  doc.text(
    `Mobile: ${
      documentConfig?.fromData?.phone ? ` ${documentConfig.fromData.phone}` : ""
    }${
      documentConfig?.fromData?.additionalPhone
        ? `, ${documentConfig.fromData.additionalPhone}`
        : ""
    }`,
    MARGIN_LEFT,
    y,
  );
  doc.setFont("Inter-Regular", "normal");
  // Check for PAN
  if (documentConfig?.fromData?.pannumber) {
    y += 4;
    doc.text(`PAN: ${documentConfig?.fromData.pannumber}`, MARGIN_LEFT, y);
  }

  // Check for FSSAI
  if (documentConfig?.fromData?.fssai) {
    y += 4;
    doc.text(`FSSAI: ${documentConfig?.fromData.fssai}`, MARGIN_LEFT, y);
  }

  // Check for CIN
  if (documentConfig?.fromData?.cinnumber) {
    y += 4;
    doc.text(`CIN: ${documentConfig?.fromData.cinnumber}`, MARGIN_LEFT, y);
  }

  doc.setFont("Inter-Regular", "normal");
  // --- Row 3: Invoice Meta Data ---
  // y += 5;
  doc.setDrawColor(200);

  // doc.line(MARGIN_LEFT, y, MARGIN_RIGHT, y);// Thin separator line
  y += 5;
  const invDate = documentConfig?.Date?.date;

  y += 4;
  const startYAddress = y;
  const invLabel = documentConfig?.numberLabel;
  doc.setFont("Inter-Bold", "bold");
  // doc.text(`Invoice #: ${ data.saleno || ''}`, 10, y);
  doc.setFontSize(9);
  doc.setFont("Inter-Bold", "bold");
  doc.text(invLabel, MARGIN_LEFT, y);
  let invNoX = PADDING + doc.getTextWidth(invLabel);

  doc.setFontSize(10);
  doc.text(`${documentConfig?.number || ""}`, invNoX, y);

  // doc.text(`Invoice Date: ${invDate || ''}`, pageW / 2.8, y);
  doc.setFontSize(9);
  const dateLabel = documentConfig?.dateLabel || "Date: ";
  const dateX = pageW / 2.8;
  doc.setFont("Inter-Bold", "bold");
  doc.text(dateLabel, dateX, y);
  let dateValX = dateX + doc.getTextWidth(dateLabel);
  doc.setFontSize(10);
  doc.text(`${invDate || ""}`, dateValX, y);

  doc.setFontSize(9);
  const time = documentConfig?.timeLabel || "Time :";
  // doc.text(`Due Date: ${invDate || ''}`, pageW - 60, y);
  const rightX = pageW - 65;
  doc.setFont("Inter-Bold", "bold");
  doc.text(time, rightX, y);
  let dueX = rightX + doc.getTextWidth(time);
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(10);
  const invDateTime = documentConfig?.Date?.time;
  doc.text(`${invDateTime || ""}`, dueX, y);
  const customerAddress = [
    [
      documentConfig?.toData?.addressline1,
      documentConfig?.toData?.addressline2,
      documentConfig?.toData?.district,
      documentConfig?.toData?.state,
      documentConfig?.toData?.country,
      documentConfig?.toData?.pincode,
    ]
      .filter(Boolean)
      .join(", "),

    // [data?.saleto?.district, data?.saleto?.state, data?.saleto?.pincode].filter(Boolean).join(", ")
  ].join("\n");
  const shippingAddress = [
    [
      documentConfig?.toData?.shippingAddress?.addressline1,
      documentConfig?.toData?.shippingAddress?.addressline2,
      documentConfig?.toData?.shippingAddress?.district,
      documentConfig?.toData?.shippingAddress?.state,
      documentConfig?.toData?.shippingAddress?.country,
      documentConfig?.toData?.shippingAddress?.pincode,
    ]
      .filter(Boolean)
      .join(", "),

    // [data?.saleto?.district, data?.saleto?.state, data?.saleto?.pincode].filter(Boolean).join(", ")
  ].join("\n");
  const hasCustomer = !!documentConfig?.toData;
  const hasAddress = !!customerAddress;
  const hasShippAddress = !!shippingAddress;
  const rowStartY = hasCustomer || hasAddress ? y + 5 : y;
  let maxColumnY = rowStartY;

  // Helper to draw Bold Label and Normal Value
  const drawLabelValue = (label, value, x, currentY) => {
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(36, 36, 36);
    doc.text(`${label}: `, x, currentY);
    const labelWidth = doc.getTextWidth(`${label}: `);
    doc.setFont("Inter-Regular", "normal");
    doc.setTextColor(0);
    doc.text(String(value), x + labelWidth, currentY);
  };

  const colWidth = 55; // Same for Billing and Shipping
  const lineStep = 4; // Standard vertical spacing for lines
  let col1X = MARGIN_LEFT;
  let col2X = pageW / 2.8;
  let col3X = pageW - 65;
  if (!hasCustomer) {
    col2X = MARGIN_LEFT;
    col3X = pageW / 2;
  }

  // 🔥 If only one column → full width feel
  if (hasCustomer && !hasAddress) {
    col2X = pageW / 2;
  }

  let col1Y = rowStartY;
  doc.setFontSize(9);
  if (hasCustomer) {
    doc.setFont("Inter-Regular", "normal");
    doc.text(documentConfig?.toLabel, MARGIN_LEFT, col1Y);
    col1Y += 5;

    doc.setFont("Inter-Bold", "bold");
    const customerName = documentConfig?.toData?.name || "-";
    const nameLines = doc.splitTextToSize(customerName, colWidth);
    doc.text(nameLines, MARGIN_LEFT, col1Y);
    col1Y += nameLines.length * lineStep;

    doc.setFont("Inter-Regular", "normal");
    doc.text(`Ph: ${documentConfig?.toData?.phone || ""}`, MARGIN_LEFT, col1Y);
    if (documentConfig?.toData?.referenceid) {
      col1Y += 4;

      doc.setFont("Inter-Regular", "normal");
      doc.text("Reference ID:", MARGIN_LEFT, col1Y);

      const labelWidth = doc.getTextWidth("Reference ID:");
      doc.text(
        ` ${documentConfig?.toData?.referenceid}`,
        MARGIN_LEFT + labelWidth,
        col1Y,
      );
    }
    if (documentConfig?.toData?.gstnumber) {
      col1Y += 4;
      drawLabelValue(
        "GSTIN",
        documentConfig?.toData.gstnumber,
        MARGIN_LEFT,
        col1Y,
      );
    }
    if (documentConfig?.toData?.fssai) {
      col1Y += 4;
      drawLabelValue(
        "FSSAI",
        documentConfig?.toData?.fssai,
        MARGIN_LEFT,
        col1Y,
      );
    }
    if (documentConfig?.toData?.pannumber) {
      col1Y += 4;
      drawLabelValue(
        "PAN",
        documentConfig?.toData.pannumber,
        MARGIN_LEFT,
        col1Y,
      );
    }
    if (documentConfig?.toData?.cinnumber) {
      col1Y += 4;
      drawLabelValue(
        "CIN",
        documentConfig?.toData.cinnumber,
        MARGIN_LEFT,
        col1Y,
      );
    }
  }
  /* --- COLUMN 2: Billing Address --- */
  let col2Y = rowStartY;

  if (hasAddress) {
    doc.setFont("Inter-Regular", "normal");
    doc.text("Billing address:", col2X, col2Y);
    col2Y += 5;

    const billLines = doc.splitTextToSize(customerAddress || "-", colWidth);
    doc.text(billLines, col2X, col2Y);
    col2Y += billLines.length * lineStep; // Actual dynamic height
  }
  /* --- COLUMN 3: Shipping Address --- */
  let col3Y = rowStartY;
  // Slightly adjusted for margin
  if (
    hasShippAddress &&    
    documentConfig?.toData?.shippingAddress?.addressline1 &&
    documentConfig?.toData?.shippingAddress?.addressline2
  ) {
    doc.setFont("Inter-Regular", "normal");
    doc.text("Shipping address:", col3X, col3Y);
    col3Y += 5;

    const shipLines = doc.splitTextToSize(shippingAddress || "-", colWidth);
    doc.text(shipLines, col3X, col3Y);
    col3Y += shipLines.length * lineStep; // Actual dynamic height
  }
  // --- THE FIX: Find the absolute lowest point of all 3 columns ---
  // maxColumnY = Math.max(col1Y, col2Y, col3Y);
  maxColumnY = Math.max(
    hasCustomer ? col1Y : rowStartY,
    hasAddress ? col2Y : rowStartY,
    hasAddress ? col3Y : rowStartY,
  );
  /* --- PLACE OF SUPPLY --- */

  y = maxColumnY + 6;
  if (documentConfig?.toData?.state) {
    doc.setFontSize(10);
    doc.setFont("Inter-Regular", "normal");
    doc.text("Place Of Supply: ", MARGIN_LEFT, y);
    const supplyWidth = doc.getTextWidth("Place Of Supply: ");
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(36, 36, 36);
    doc.text(
      documentConfig?.toData?.state || "-",
      MARGIN_LEFT + supplyWidth,
      y,
    );

    // Final safety margin before table
    y += 4;
  }
  const leftBoxX = 8;

  /* -------------------------------------------------
     PRODUCT TABLE
  ------------------------------------------------- */
  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { top: PADDING, left: PADDING, right: PADDING, bottom: PADDING }, // Balanced margins for symmetry
    // tableWidth: 'auto',
    styles: {
      font: "Inter-Regular",
      fontSize: 8,
      cellPadding: 2, // Extra vertical padding for clarity
      valign: "middle",
      lineHeight: 1.5,
      lineColor: selectedTheme?.templateTheme?.borderColor,
      textColor: [0, 0, 0], // True black text
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 9,
      // textColor: [0, 0, 0],
      // fillColor: false,
      fillColor: selectedTheme?.templateTheme?.primaryColor || [20, 40, 100], // Navy blue fill
      textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
      lineWidth: { top: 0.1, bottom: 0.1 },
      lineColor: selectedTheme?.templateTheme?.borderColor,
    },
    head: [["#", "Item", "MRP", "Selling Price", "Qty", "Amount"]],
    body: documentConfig?.items?.map((it, i) => {
      // Exact Item + HSN formatting like image_f6047a.png
      const itemDesc = it.hsnsac
        ? `${it.title}\n${it.imode === "SERVICE" ? "SAC" : "HSN"}: ${it.hsnsac}`
        : it.title;
      const amount = it.lineGrandTotal;
      const mrp = it.mrp;
      const baseprice = it.baseprice;
      const landingCost = it?.lineTotalLandingCost;
      return [
        i + 1,
        itemDesc,
        `${mrp}`,
        `${baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`, //Pcs
        `${amount}`,
      ];
    }),
    columnStyles: {
      0: { cellWidth: 10, halign: "center" }, // S.No
      1: { cellWidth: "auto", halign: "left" }, // Flexible Description
      2: { cellWidth: 25, halign: "right" }, // Right-aligned Price
      3: { cellWidth: 25, halign: "right" },
      4: { cellWidth: 20, halign: "center" }, // Right-aligned Price
      5: { cellWidth: 25, halign: "right" }, // Amount
    },
    didParseCell: function (data) {
      // Header alignment
      if (data.section === "head") {
          const colId = data.column.index;
        if ([0,1,2, 3,4,5].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }
      // Body row styling
      if (data.section === "body") {
        // Light gray divider lines
        data.cell.styles.lineWidth = { bottom: 0.1 };
      }
    },
  });

  y += 3;

  const igst = documentConfig?.igstAmount;
  const sgst = documentConfig?.sgstAmount;
  const cgst = documentConfig?.cgstAmount;
  const linetotalbaseprice = documentConfig?.totalBasePrice;
  const grantotal = documentConfig?.grandTotal;
  

  /* -------------------------------------------------
   TOTALS & GST SECTION (Right Side)
------------------------------------------------- */
  y = doc.lastAutoTable.finalY + 5; // Start after the table

  // 1. Grand Total Row
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(11);

  const totalTextX = 140;
  const totalValueX = 200; // This is your right-aligned anchor

  doc.text("Total", totalTextX, y);
  doc.text(`${grantotal}`, MARGIN_RIGHT, y, {
    align: "right",
  });

  // FIX: The line must end exactly at 200 to match your text alignment
  y += 2;
  doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
  doc.setLineWidth(0.1);
  doc.line(totalTextX, y, MARGIN_RIGHT, y); // Line now matches text width perfectly
  // 2. Tax Breakdown
  y += 6;
  doc.setFont("Inter-Regular", "normal");

  doc.setFontSize(9);
  // doc.setTextColor(0);

  const summaryData = [["Total Amount Before Tax", `${linetotalbaseprice}`]];
  if (isInterState) {
    summaryData.push(["IGST", `${igst}`]);
  } else {
    summaryData.push(["CGST", `${cgst}`]);
    summaryData.push(["SGST", `${sgst}`]);
  }
  summaryData.forEach(([label, value]) => {
    doc.text(label, 140, y);
    doc.text(value, MARGIN_RIGHT, y, { align: "right" });
    y += 5;
  });
  doc.setTextColor(64, 64, 64);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);

  doc.text(`Total Items  : ${documentConfig?.totalItems || 0} `, 10, y);
  doc.setTextColor(0);
  const amountWords = documentConfig?.amountInWords || "";
  doc.text(`Total Amount (In Words): ${amountWords}`, MARGIN_RIGHT, y, {
    align: "right",
  });

  y += 2;
  doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);

  // doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.1);
  doc.line(PADDING, y, MARGIN_RIGHT, y);

  y += 5;

  doc.setTextColor(0);
  const footerStartY = y;
  doc.setFontSize(9);

  doc.setFont("Inter-Bold", "bold");
  doc.setTextColor(32, 32, 32);
  y = footerStartY;

  const payX = 140;
 
  const payments = [
    ["Amount Payable:", `${data?.balanceDue}`],
    ["Amount Paid:",  documentConfig?.paidAmount],
  ];

  payments.forEach(([label, value]) => {
    if (label === "Amount Payable:") {
      doc.setFont("Inter-Bold", "bold");
    } else {
      doc.setFont("Inter-Regular", "normal");
    }
    doc.text(label, payX, y);
    doc.text(value, MARGIN_RIGHT, y, { align: "right" });
    y += 4;
  });

  let neededFooterHeight = 0;
  if (hasPayment) {
    if (parseAmount(documentConfig?.cashPaid) > 0) neededFooterHeight += 4;
    if (parseAmount(documentConfig?.upiPaid) > 0) neededFooterHeight += 4;
    if (parseAmount(documentConfig?.cardPaid) > 0) neededFooterHeight += 4;
    if (parseAmount(documentConfig?.onlinePaid) > 0) neededFooterHeight += 4;
  }
  neededFooterHeight += 35;
  if (y + neededFooterHeight > pageH - 20) {
    doc.addPage();
    y = PADDING + 5;
  }
  doc.setFontSize(8);
  y += 2;
  doc.setFont("Inter-Bold", "bold");
  const drawPaymentLine = (amount, label, currentY) => {
    doc.setFont("Inter-Regular", "normal");
    const labelWidth = doc.getTextWidth(label);
    doc.text(label, MARGIN_RIGHT, currentY, { align: "right" });

    doc.setFont("Inter-Bold", "bold");
    doc.text(`${amount} `, MARGIN_RIGHT - labelWidth, currentY, {
      align: "right",
    });
  };
  if (hasPayment) {
    if (parseAmount(documentConfig?.cashPaid) > 0) {
      drawPaymentLine(data.cashPaid, "Paid via Cash", y);
    }

    if (parseAmount(documentConfig?.upiPaid) > 0) {
      y += 4;
      drawPaymentLine(data.upiPaid, "Paid via UPI", y);
    }

    if (parseAmount(documentConfig?.cardPaid) > 0) {
      y += 4;
      drawPaymentLine(data.cardPaid, "Paid via Card", y);
    }

    if (parseAmount(documentConfig?.onlinePaid) > 0) {
      y += 4;
      drawPaymentLine(data.onlinePaid, "Paid Online", y);
    }
    y += 8;
  }

  const footerBlockHeight = 25;
  const spaceRemaining = pageH - y;

  // If remaining space is less than the footer height, jump to a new page
  if (spaceRemaining < footerBlockHeight) {
    doc.addPage();
    y = PADDING + 5;
  }
  const currentY = y;

  const paymentSettings = data?.settings;

  const upiId = storeSettings?.paymentSettings?.upiId || "";
  const payeeName = documentConfig?.bankDetails?.beneficiaryName || "";
  const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
    payeeName,
  )}&cu=INR`;
  const hasUpi = upiId && upiId.trim().length > 0;
  const bankX = hasUpi ? 45 : PADDING;
  // console.log("bankX", bankX);
  doc.setFontSize(10);
  doc.setTextColor(56, 56, 56);
  doc.setFont("Inter-Bold", "bold");

  if (upiId && upiId.trim() !== "") {
    let footerY = y;
    doc.text("Pay using UPI:", PADDING - 2, footerY);
    const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
      errorCorrectionLevel: "L",
      scale: 4,
      margin: 1,
    });
    const qrX = PADDING - 2;
    const qrY = footerY + 2;
    const qrSize = 25;
    doc.addImage(qrCodeDataUrl, "JPEG", qrX, qrY, qrSize, qrSize);
  }

  doc.setFontSize(10);
  doc.setTextColor(56, 56, 56);
  doc.setFont("Inter-Bold", "bold");
  let bankY = currentY + 5;
  if (documentConfig?.bankDetails) {
    doc.text("Bank Details:", bankX, currentY);

    const bankDetails = [
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],
      ["UPI ID:", documentConfig?.upiId || ""],
    ].filter(([_, value]) => Boolean(value));

    bankDetails.forEach(([label, value]) => {
      doc.setFont("Inter-Regular", "normal");
      doc.text(label, bankX, bankY);
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(value), bankX + 30, bankY);
      bankY += 5;
    });
  }

  let sigY = currentY;
  const rightX1 = 202;

  doc.setFontSize(6);
  let actualImgH = 0;
  let signBlockY = sigY;
  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
      const base64 = await urlToBase64(signimg);
      const { width, height } = await loadImageDimensions(base64);
      const maxWidth = 35;
      const maxHeight = 20;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const imgW = width * ratio;
      const imgH = height * ratio;
      doc.addImage(
        base64,
        signimg.endsWith(".png") ? "PNG" : "JPEG",
        MARGIN_RIGHT - imgW + 3,
        signBlockY,
        imgW,
        imgH,
      );
    } catch {
      console.warn("Signature load failed");
    }
  }
  // sigY += 25;
  const sigTextY = signBlockY + (actualImgH > 0 ? actualImgH + 2 : 10);
  doc.setFontSize(8);

  y = Math.max(bankY, sigY) + 5;
  y += 6;

  doc.setTextColor(0);

  doc.setFont("Inter-Regular", "normal");
  doc.text("Authorized Signatory" || "", MARGIN_RIGHT, signBlockY + 14, {
    align: "right",
  });
  // const rowBottomY = Math.max(bankY, currentY + 27, signBlockY + 10);
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);
  //  y = rowBottomY +3 ;
  const rowBottomY = Math.max(bankY, currentY + 12, sigTextY);

  y = rowBottomY + 5; // Only a 4mm gap before Notes

  // REDUCED BUFFER: Only jump to new page if within 1.5cm of bottom
  if (y > pageH - 20) {
    doc.addPage();
    y = PADDING + 5;
  }

  if (documentConfig?.remarks) {
    y += 4;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.setTextColor(72, 72, 72);

    // Check page break for the Remark heading
    if (y > pageH - 30) {
      doc.addPage();
      y = PADDING + 5;
    }

    doc.text("Remark", PADDING, y);

    const innerPaddingX = 2;
    const maxTextWidth = pageW - PADDING * 2 - innerPaddingX * 2 + 25;

    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig?.remarks),
      maxTextWidth,
    );

    const textLineHeight = 4.0;
    const boxPaddingTop = 2.5;
    const boxPaddingBottom = 2.5;

    const remarkHeight =
      boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;
    const boxStartY = y + 2.5;

    // Draw Remark Box Container
    doc.setDrawColor(180);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);
    doc.setTextColor(0);

    let remarkY = boxStartY + boxPaddingTop;

    remarkLines.forEach((line) => {
      if (remarkY > pageH - PADDING) {
        doc.addPage();
        remarkY = PADDING + 6;
      }
      doc.text(line, PADDING, remarkY);
      remarkY += textLineHeight;
    });

    // Update y to sit right below the Remark container with a 5mm spacer
    y = boxStartY + remarkHeight;
  }
  if (terms?.length > 0 && !isPreSale) {
    y += 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    // doc.setTextColor(36, 36, 36);
    if (y > pageH - 25) {
      doc.addPage();
      y = PADDING + 5; // Reset to top of new page
    }
    doc.setTextColor(72, 72, 72);
    doc.text("Terms & Conditions", PADDING, y);
    doc.setTextColor(0);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8.5);

    // 1. Split text into lines
    const termLines = doc.splitTextToSize(terms, pageW - 20);

    // 2. Define your custom line height (distance between points)
    const customLineHeight = 5; // Increase this number to get more space between lines

    let currentTermY = y + 5; // Start position for the first line

    // 3. Loop through lines to draw them one by one with extra space
    termLines.forEach((line) => {
      // Check if we need a new page before drawing the line
      if (currentTermY > pageH - 20) {
        doc.addPage();
        currentTermY = 10; // Reset Y for the new page
      }

      doc.text(line, PADDING, currentTermY);
      currentTermY += customLineHeight; // This creates the height/distance you want
    });

    y = currentTermY + 5; // Update leftY for whatever comes next
  }

  // draw table ONLY if rows exist

  if (storeSettings?.negobillBranding) {
    // console.log("ghu")
    const y = pageH - 5;
    const rightX = pageW / 2 - 10;

    // Text parts
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";

    doc.setFontSize(8);

    // Measure widths
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);

    doc.setFont("Inter-Bold", "bold");

    // Start X so whole string is right-aligned
    const startX = rightX;

    // Draw normal part
    doc.setFont("Inter-Regular", "normal");
    doc.text(normalText, startX, y, {
      align: "center",
    });

    // Draw bold part
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, startX + 1 + normalW, y, {
      align: "center",
    });
  }
  // setDownload(false);
  return doc;
};

export const generateInvComBillingPdf = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const { documentConfig,hasPayment,  isPreSale } =
    invoiceConfig;
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  // Header Logic
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const COL_RIGHT = pageW - 65;
  const COL_MIDDLE = pageW / 2.8;
  let y = PADDING + 5;
  const invoiceText =
    data?.status === "CANCELLED" ? "CANCELLED" : "BILL RECIEP";
  const originalText = "ORIGINAL FOR RECIPIENT";
  function drawSpacedText(doc, text, x, y, spacing = 0.8) {
    let cursorX = x;
    for (const ch of text) {
      doc.text(ch, cursorX, y);
      cursorX += doc.getTextWidth(ch) + spacing;
    }
  }
  // --- Row 1: Header Titles ---
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(14);
  doc.setTextColor(20, 40, 100); // Navy blue for "TAX INVOICE"
  // doc.text(invoiceText, MARGIN_LEFT, y);
  drawSpacedText(doc, invoiceText, MARGIN_LEFT, y, 0.6);

  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90); // Gray
  // doc.text(originalText, MARGIN_RIGHT, y, { align: "right" });
  const textWidth = originalText
    .split("")
    .reduce((w, ch) => w + doc.getTextWidth(ch) + 0.6, 0);

  drawSpacedText(doc, originalText, MARGIN_RIGHT - textWidth, y, 0.6);

  const address = joinAddress(
    documentConfig?.fromData?.addressline1,
    documentConfig?.fromData?.addressline2,
    documentConfig?.fromData?.district,
    documentConfig?.fromData?.state,
    documentConfig?.fromData?.pincode,
  );

  // --- Row 2: Seller Info & Logo ---
  y = 20;
  doc.setFontSize(13);
  doc.setTextColor(0);
  doc.text(documentConfig?.fromData?.title || "", PADDING, y);

  // Placeholder for Logo (Replace with doc.addImage(base64, 'PNG', x, y, w, h))
  doc.setFontSize(22);
  doc.setTextColor(34, 139, 34); // DMart Green
  if (documentConfig?.image) {
    try {
      const imagePath =  documentConfig?.image;

      // const imagePath = settings?.saleProfileData?.image ?  settings?.saleProfileData?.image : "/assets/images/logo-light.png";
      const base64 = await urlToBase64(imagePath);
      const { width, height } = await loadImageDimensions(base64);

      const maxWidth = 40;
      const maxHeight = 10;

      let imgW = width;
      let imgH = height;

      const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);

      const finalW = imgW * ratio;
      const finalH = imgH * ratio;

      const ext = imagePath.split(".").pop().toLowerCase();
      const format = ext === "png" ? "PNG" : "JPEG";
      const logoX = MARGIN_RIGHT - finalW;
      // Use finalW and finalH instead of maxWidth and maxHeight
      doc.addImage(base64, format, logoX, y, finalW, finalH);
      // doc.addImage(base64, format, pageW - 60, y - 5, maxWidth, maxHeight);
    } catch (err) {
      console.warn("Logo load failed:", err);
      // Fallback text if logo fails
      doc.setFontSize(22);
      doc.setTextColor(34, 139, 34);
      doc.text("Test-Store", MARGIN_RIGHT, y + 5, { align: "right" });
    }
  }

  doc.setFontSize(9);
  doc.setFont("Inter-Bold", "bold");
  doc.setTextColor(0);
  // y += 6;
  // doc.text(`GSTIN: ${data?.salefrom?.gstnumber || ""}`, MARGIN_LEFT, y);
  y += 4;
  doc.setFont("Inter-Regular", "normal");
  const addressWidth = pageW / 2 - MARGIN_LEFT - 5;
  const addressLines = doc.splitTextToSize(address, addressWidth);
  doc.text(addressLines, MARGIN_LEFT, y);
  y += addressLines.length * 4;

  doc.text(
    `Mobile: ${
      documentConfig?.fromData?.phone ? ` ${documentConfig.fromData.phone}` : ""
    }${
      documentConfig?.fromData?.additionalPhone
        ? `, ${documentConfig.fromData.additionalPhone}`
        : ""
    }`,
    MARGIN_LEFT,
    y,
  );
  doc.setFont("Inter-Regular", "normal");
  // Check for PAN
  if (documentConfig?.fromData?.pannumber) {
    y += 4;
    doc.text(`PAN: ${documentConfig?.fromData.pannumber}`, MARGIN_LEFT, y);
  }

  // Check for FSSAI
  if (documentConfig?.fromData?.fssai) {
    y += 4;
    doc.text(`FSSAI: ${documentConfig?.fromData.fssai}`, MARGIN_LEFT, y);
  }

  // Check for CIN
  if (documentConfig?.fromData?.cinnumber) {
    y += 4;
    doc.text(`CIN: ${documentConfig?.fromData.cinnumber}`, MARGIN_LEFT, y);
  }

  doc.setFont("Inter-Regular", "normal");
  // --- Row 3: Invoice Meta Data ---
  // y += 5;
  doc.setDrawColor(200);

  // doc.line(MARGIN_LEFT, y, MARGIN_RIGHT, y);// Thin separator line
  y += 5;
  const invDate = documentConfig?.Date?.date;
  const invDateTime = documentConfig?.Date?.time;
  y += 4;
  const startYAddress = y;

  doc.setFont("Inter-Bold", "bold");
  // doc.text(`Invoice #: ${ data.saleno || ''}`, 10, y);
  doc.setFontSize(9);
  doc.setFont("Inter-Bold", "bold");
  doc.text(documentConfig?.numberLabel, MARGIN_LEFT, y);
  let invNoX = PADDING + doc.getTextWidth(documentConfig?.numberLabel);

  doc.setFontSize(10);
  doc.text(`${documentConfig?.number || ""}`, invNoX, y);

  // doc.text(`Invoice Date: ${invDate || ''}`, pageW / 2.8, y);
  doc.setFontSize(9);
  const dateLabel = documentConfig?.dateLabel || "Date: ";
  const dateX = pageW / 2.8;
  doc.setFont("Inter-Bold", "bold");
  doc.text(dateLabel, dateX, y);
  let dateValX = dateX + doc.getTextWidth(dateLabel);
  doc.setFontSize(10);
  doc.text(`${invDate || ""}`, dateValX, y);

  doc.setFontSize(9);
  const time = documentConfig?.timeLabel || "Time : ";
  // doc.text(`Due Date: ${invDate || ''}`, pageW - 60, y);
  const rightX = pageW - 65;
  doc.setFont("Inter-Bold", "bold");
  doc.text(time, rightX, y);
  let dueX = rightX + doc.getTextWidth(time);
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(10);
  doc.text(`${invDateTime || ""}`, dueX, y);
  const customerAddress = [
    [
      documentConfig?.toData?.addressline1,
      documentConfig?.toData?.addressline2,
      documentConfig?.toData?.district,
      documentConfig?.toData?.state,
      documentConfig?.toData?.country,
      documentConfig?.toData?.pincode,
    ]
      .filter(Boolean)
      .join(", "),

    // [data?.saleto?.district, data?.saleto?.state, data?.saleto?.pincode].filter(Boolean).join(", ")
  ].join("\n");
  const shippingAddress = [
    [
      documentConfig?.toData?.shippingAddress?.addressline1,
      documentConfig?.toData?.shippingAddress?.addressline2,
      documentConfig?.toData?.shippingAddress?.district,
      documentConfig?.toData?.shippingAddress?.state,
      documentConfig?.toData?.shippingAddress?.country,
      documentConfig?.toData?.shippingAddress?.pincode,
    ]
      .filter(Boolean)
      .join(", "),

    // [data?.saleto?.district, data?.saleto?.state, data?.saleto?.pincode].filter(Boolean).join(", ")
  ].join("\n");
  const hasCustomer = !!documentConfig?.toData;
  const hasAddress = !!customerAddress;
  const hasShipAddress = !!shippingAddress;
  const rowStartY = hasCustomer || hasAddress ? y + 5 : y;

  // const rowStartY = y + 5;
  let maxColumnY = rowStartY;

  // Helper to draw Bold Label and Normal Value
  const drawLabelValue = (label, value, x, currentY) => {
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(36, 36, 36);
    doc.text(`${label}: `, x, currentY);
    const labelWidth = doc.getTextWidth(`${label}: `);
    doc.setFont("Inter-Regular", "normal");
    doc.setTextColor(0);
    doc.text(String(value), x + labelWidth, currentY);
  };

  const colWidth = 55; // Same for Billing and Shipping
  const lineStep = 4; // Standard vertical spacing for lines
  let col1X = MARGIN_LEFT;
  let col2X = pageW / 2.8;
  let col3X = pageW - 65;
  if (!hasCustomer) {
    col2X = MARGIN_LEFT;
    col3X = pageW / 2;
  }
  if (hasCustomer && !hasAddress) {
    col2X = pageW / 2;
  }
  /* --- COLUMN 1: Customer Details --- */
  let col1Y = rowStartY;
  doc.setFontSize(9);
  if (hasCustomer) {
    doc.setFont("Inter-Regular", "normal");
    doc.text(documentConfig?.toLabel, MARGIN_LEFT, col1Y);
    col1Y += 5;

    doc.setFont("Inter-Bold", "bold");
    const customerName = documentConfig?.toData?.name || "-";
    const nameLines = doc.splitTextToSize(customerName, colWidth);
    doc.text(nameLines, PADDING, col1Y);
    col1Y += nameLines.length * lineStep;

    doc.setFont("Inter-Regular", "normal");
    doc.text(`Ph: ${documentConfig?.toData?.phone || ""}`, MARGIN_LEFT, col1Y);

    // if (data?.saleto?.gstnumber) {
    //   col1Y += 4;
    //   drawLabelValue("GSTIN", data.saleto.gstnumber, 10, col1Y);
    // }
    if (documentConfig?.toData?.referenceid) {
      col1Y += 4;

      doc.setFont("Inter-Regular", "normal");
      doc.text("Reference ID:", MARGIN_LEFT, col1Y);

      const labelWidth = doc.getTextWidth("Reference ID:");
      doc.text(
        ` ${documentConfig?.toData.referenceid}`,
        MARGIN_LEFT + labelWidth,
        col1Y,
      );
    }
    if (documentConfig?.toData?.gstnumber) {
      col1Y += 4;
      drawLabelValue(
        "GSTIN",
        documentConfig?.toData.gstnumber,
        MARGIN_LEFT,
        col1Y,
      );
    }
    if (documentConfig?.toData?.fssai) {
      col1Y += 4;
      drawLabelValue("FSSAI", documentConfig?.toData.fssai, MARGIN_LEFT, col1Y);
    }
    if (documentConfig?.toData?.pannumber) {
      col1Y += 4;
      drawLabelValue(
        "PAN",
        documentConfig?.toData.pannumber,
        MARGIN_LEFT,
        col1Y,
      );
    }
    if (documentConfig?.toData?.cinnumber) {
      col1Y += 4;
      drawLabelValue(
        "CIN",
        documentConfig?.toData.cinnumber,
        MARGIN_LEFT,
        col1Y,
      );
    }
  }
  /* --- COLUMN 2: Billing Address --- */
  let col2Y = rowStartY;

  if (hasAddress) {
    doc.setFont("Inter-Regular", "normal");
    doc.text("Billing address:", col2X, col2Y);
    col2Y += 5;

    const billLines = doc.splitTextToSize(customerAddress || "-", colWidth);
    doc.text(billLines, col2X, col2Y);
    col2Y += billLines.length * lineStep; // Actual dynamic height
  }
  /* --- COLUMN 3: Shipping Address --- */
  let col3Y = rowStartY;
  if (
    hasShipAddress &&
    documentConfig?.toData?.shippingAddress?.addressline1 &&
    documentConfig?.toData?.shippingAddress?.addressline2
  ) {
    const col3X = pageW - 65; // Slightly adjusted for margin
    doc.setFont("Inter-Regular", "normal");
    doc.text("Shipping address:", col3X, col3Y);
    col3Y += 5;

    const shipLines = doc.splitTextToSize(shippingAddress || "-", colWidth);
    doc.text(shipLines, col3X, col3Y);
    col3Y += shipLines.length * lineStep; // Actual dynamic height
  }
  maxColumnY = Math.max(
    hasCustomer ? col1Y : rowStartY,
    hasAddress ? col2Y : rowStartY,
    hasAddress ? col3Y : rowStartY,
  );
  /* --- PLACE OF SUPPLY --- */

  y = maxColumnY + 5;
  if (documentConfig?.toData?.state) {
    doc.setFontSize(10);
    doc.setFont("Inter-Regular", "normal");
    doc.text("Place Of Supply: ", MARGIN_LEFT, y);
    const supplyWidth = doc.getTextWidth("Place Of Supply: ");
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(36, 36, 36);
    doc.text(
      documentConfig?.toData?.state || "-",
      MARGIN_LEFT + supplyWidth,
      y,
    );
    // Final safety margin before table
    y += 4;
  }
  const leftBoxX = 8;

  /* -------------------------------------------------
     PRODUCT TABLE
  ------------------------------------------------- */
  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { left: MARGIN_LEFT, right: MARGIN_LEFT }, // Balanced margins for symmetry
    // tableWidth: 'auto',
    styles: {
      font: "Inter-Regular",
      fontSize: 8,
      cellPadding: 2, // Extra vertical padding for clarity
      valign: "middle",
      lineHeight: 1.5,
      lineColor: selectedTheme?.templateTheme?.borderColor,
      textColor: [0, 0, 0], // True black text
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 9,
      // textColor: [0, 0, 0],
      // fillColor: false,
      fillColor: selectedTheme?.templateTheme?.primaryColor || [20, 40, 100], // Navy blue fill
      textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
      lineWidth: { top: 0.1, bottom: 0.1 },
      lineColor: selectedTheme?.templateTheme?.borderColor,
    },
    head: [["#", "Item", "MRP", "Selling Price", "Qty", "Amount"]],
    body: documentConfig?.items.map((it, i) => {
      // Exact Item + HSN formatting like image_f6047a.png
      const itemDesc = it.hsnsac
        ? `${it.title}\n${it.imode === "SERVICE" ? "SAC" : "HSN"}: ${it.hsnsac}`
        : it.title;
      const amount = it.lineGrandTotal;
      const mrp = it.mrp;
      const baseprice = it.baseprice;
      const landingCost = it?.lineTotalLandingCost;
      return [
        i + 1,
        itemDesc,
        `${mrp}`,
        `${baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`, //Pcs
        `${amount}`,
      ];
    }),
    columnStyles: {
      0: { cellWidth: 10, halign: "center" }, // S.No
      1: { cellWidth: "auto", halign: "left" }, // Flexible Description
      2: { cellWidth: 25, halign: "right" }, // Right-aligned Price
      3: { cellWidth: 25, halign: "right" },
      4: { cellWidth: 20, halign: "center" }, // Right-aligned Price
      5: { cellWidth: 25, halign: "right" }, // Amount
    },
    didParseCell: function (data) {
      // Header alignment
       if (data.section === "head") {
          const colId = data.column.index;
        if ([0,1,2, 3,4,5].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }

      if (data.section === "body") {
        data.cell.styles.lineWidth = { bottom: 0.1 };
      }
    },
  });

  y += 3;

  const grantotal = documentConfig?.grandTotal;
 

  /* -------------------------------------------------
   TOTALS & GST SECTION (Right Side)
------------------------------------------------- */
  y = doc.lastAutoTable.finalY + 5; // Start after the table

  // 1. Grand Total Row
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(11);

  const totalTextX = 140;
  const totalValueX = 200; // This is your right-aligned anchor

  doc.text("Total", totalTextX, y);
  doc.text(`${grantotal}`, MARGIN_RIGHT, y, {
    align: "right",
  });

  // FIX: The line must end exactly at 200 to match your text alignment
  y += 2;
  doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
  doc.setLineWidth(0.1);
  doc.line(totalTextX, y, MARGIN_RIGHT, y); // Line now matches text width perfectly
  // 2. Tax Breakdown
  y += 6;
  // doc.setFont("Inter-Regular", "normal");
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(9);
  // doc.setTextColor(0);
  doc.setTextColor(48, 48, 48); // #303030

  // const summaryData = [
  //   ["Total Amount Before Tax", `${formatCurrencyINR(linetotalbaseprice)}`],
  //   ["CGST ", `${formatCurrencyINR(cgst)}`],
  //   ["SGST ", `${formatCurrencyINR(sgst)}`],
  // ];

  // summaryData.forEach(([label, value]) => {
  //   doc.text(label, 140, y);
  //   doc.text(value, MARGIN_RIGHT, y, { align: "right" });
  //   y += 5;
  // });
  doc.setTextColor(64, 64, 64);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);

  doc.text(`Total Items  : ${documentConfig?.totalItems || 0} `, PADDING, y);
  doc.setTextColor(0);
  const amountWords = documentConfig?.amountInWords || "";
  doc.text(`Total amount (in words): INR ${amountWords}`, MARGIN_RIGHT, y, {
    align: "right",
  });

  y += 2;

  doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
  doc.setLineWidth(0.1);
  doc.line(PADDING, y, MARGIN_RIGHT, y);

  y += 5;

  doc.setTextColor(0);
  const footerStartY = y;
  doc.setFontSize(9);

  doc.setFont("Inter-Bold", "bold");
  doc.setTextColor(32, 32, 32);
  y = footerStartY;
 
  const payX = 140;
  const payments = [
    ["Amount Payable:", `${documentConfig?.balanceDue}`],
    ["Amount Paid:", documentConfig?.paidAmount],
  ];

  payments.forEach(([label, value]) => {
    if (label === "Amount Payable:") {
      doc.setFont("Inter-Bold", "bold");
    } else {
      doc.setFont("Inter-Regular", "normal");
    }
    doc.text(label, payX, y);
    doc.text(value, MARGIN_RIGHT, y, { align: "right" });
    y += 4;
  });

  let neededFooterHeight = 0;
  if (hasPayment) {
    if (parseAmount(documentConfig?.cashPaid) > 0) neededFooterHeight += 4;
    if (parseAmount(documentConfig?.upiPaid) > 0) neededFooterHeight += 4;
    if (parseAmount(documentConfig?.cardPaid) > 0) neededFooterHeight += 4;
    if (parseAmount(documentConfig?.onlinePaid) > 0) neededFooterHeight += 4;
  }
  neededFooterHeight += 35;
  if (y + neededFooterHeight > pageH - 20) {
    doc.addPage();
    y = 10;
  }
  doc.setFontSize(8);
  y += 2;
  doc.setFont("Inter-Bold", "bold");
  const drawPaymentLine = (amount, label, currentY) => {
    doc.setFont("Inter-Regular", "normal");
    const labelWidth = doc.getTextWidth(label);
    doc.text(label, MARGIN_RIGHT, currentY, { align: "right" });

    // 2. Set font to Bold to draw the amount just to the left of the label
    doc.setFont("Inter-Bold", "bold");
    doc.text(`${amount} `, MARGIN_RIGHT - labelWidth, currentY, {
      align: "right",
    });
  };
  if (hasPayment) {
    if (parseAmount(documentConfig?.cashPaid) > 0) {
      drawPaymentLine(data.cashPaid, "Paid via Cash", y);
    }

    if (parseAmount(documentConfig?.upiPaid) > 0) {
      y += 4;
      drawPaymentLine(data.upiPaid, "Paid via UPI", y);
    }

    if (parseAmount(documentConfig?.cardPaid) > 0) {
      y += 4;
      drawPaymentLine(data.cardPaid, "Paid via Card", y);
    }

    if (parseAmount(documentConfig?.onlinePaid) > 0) {
      y += 4;
      drawPaymentLine(data.onlinePaid, "Paid Online", y);
    }
    y += 8;
  }

  const footerBlockHeight = 25;
  const spaceRemaining = pageH - y;

  // If remaining space is less than the footer height, jump to a new page
  if (spaceRemaining < footerBlockHeight) {
    doc.addPage();
    y = 10;
  }
  const currentY = y;

  // const paymentSettings = data?.settings;

  const upiId = storeSettings?.paymentSettings?.upiId || "";
  const payeeName = documentConfig?.bankDetails?.beneficiaryName || "";
  const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
    payeeName,
  )}&cu=INR`;
  const hasUpi = upiId && upiId.trim().length > 0;
  const bankX = hasUpi ? 45 : PADDING;
  // console.log("bankX", bankX);
  doc.setFontSize(10);
  doc.setTextColor(56, 56, 56);
  doc.setFont("Inter-Bold", "bold");

  if (upiId && upiId.trim() !== "") {
    let footerY = y;
    doc.text("Pay using UPI:", PADDING - 2, footerY);
    const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
      errorCorrectionLevel: "L",
      scale: 4,
      margin: 1,
    });
    const qrX = PADDING - 2;
    const qrY = footerY + 2;
    const qrSize = 25;
    doc.addImage(qrCodeDataUrl, "JPEG", qrX, qrY, qrSize, qrSize);
  }

  doc.setFontSize(10);
  doc.setTextColor(56, 56, 56);
  doc.setFont("Inter-Bold", "bold");
  let bankY = currentY + 5;
  if (documentConfig?.bankDetails) {
    doc.text("Bank Details:", bankX, currentY);

    const bankDetails = [
     
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],
      ["UPI ID:", documentConfig?.upiId || ""],
    ].filter(([_, value]) => Boolean(value));

    bankDetails.forEach(([label, value]) => {
      doc.setFont("Inter-Regular", "normal");
      doc.text(label, bankX, bankY);
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(value), bankX + 30, bankY);
      bankY += 5;
    });
  }

  let sigY = currentY;
  const rightX1 = 202;

  doc.setFontSize(6);
  let actualImgH = 0;
  let signBlockY = sigY;
  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
      const base64 = await urlToBase64(signimg);
      const { width, height } = await loadImageDimensions(base64);
      const maxWidth = 35;
      const maxHeight = 20;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const imgW = width * ratio;
      const imgH = height * ratio;
      doc.addImage(
        base64,
        signimg.endsWith(".png") ? "PNG" : "JPEG",
        MARGIN_RIGHT - imgW + 3,
        signBlockY,
        imgW,
        imgH,
      );
    } catch {
      console.warn("Signature load failed");
    }
  }
  // sigY += 25;
  const sigTextY = signBlockY + (actualImgH > 0 ? actualImgH + 2 : 10);
  doc.setFontSize(8);

  y = Math.max(bankY, sigY) + 5;
  y += 6;

  doc.setTextColor(0);

  doc.setFont("Inter-Regular", "normal");
  doc.text("Authorized Signatory" || "", MARGIN_RIGHT, signBlockY + 14, {
    align: "right",
  });
  // const rowBottomY = Math.max(bankY, currentY + 27, signBlockY + 10);
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);
  //  y = rowBottomY +3 ;
  const rowBottomY = Math.max(bankY, currentY + 10, sigTextY);

  y = rowBottomY + 5; // Only a 4mm gap before Notes

  // REDUCED BUFFER: Only jump to new page if within 1.5cm of bottom
  if (y > pageH - 20) {
    doc.addPage();
    y = 10;
  }

  y += 4;
  if (documentConfig?.remarks) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.setTextColor(72, 72, 72);

    // Check page break for the Remark heading
    if (y > pageH - 30) {
      doc.addPage();
      y = PADDING + 5;
    }

    doc.text("Remark", PADDING, y);

    const innerPaddingX = 2;
    const maxTextWidth = pageW - PADDING * 2 - innerPaddingX * 2 + 25;

    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig.remarks),
      maxTextWidth,
    );

    const textLineHeight = 4.0;
    const boxPaddingTop = 2.5;
    const boxPaddingBottom = 2.5;

    const remarkHeight =
      boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;
    const boxStartY = y + 2.5;

    // Draw Remark Box Container
    doc.setDrawColor(180);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);
    doc.setTextColor(0);

    let remarkY = boxStartY + boxPaddingTop;

    remarkLines.forEach((line) => {
      if (remarkY > pageH - PADDING) {
        doc.addPage();
        remarkY = PADDING + 6;
      }
      doc.text(line, PADDING, remarkY);
      remarkY += textLineHeight;
    });

    // Update y to sit right below the Remark container with a 5mm spacer
    y = boxStartY + remarkHeight;
  }
  if (terms?.length > 0 && !isPreSale) {
    y += 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    // doc.setTextColor(36, 36, 36);
    if (y > pageH - 25) {
      doc.addPage();
      y = PADDING + 5; // Reset to top of new page
    }
    doc.setTextColor(72, 72, 72);
    doc.text("Terms & Conditions", PADDING, y);
    doc.setTextColor(0);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8.5);

    // 1. Split text into lines
    const termLines = doc.splitTextToSize(terms, pageW - 20);

    // 2. Define your custom line height (distance between points)
    const customLineHeight = 5; // Increase this number to get more space between lines

    let currentTermY = y + 5; // Start position for the first line

    // 3. Loop through lines to draw them one by one with extra space
    termLines.forEach((line) => {
      // Check if we need a new page before drawing the line
      if (currentTermY > pageH - 20) {
        doc.addPage();
        currentTermY = 10; // Reset Y for the new page
      }

      doc.text(line, PADDING, currentTermY);
      currentTermY += customLineHeight; // This creates the height/distance you want
    });

    y = currentTermY + 5; // Update leftY for whatever comes next
  }

  // draw table ONLY if rows exist

  if (storeSettings?.negobillBranding) {
    const y = pageH - 5;
    const rightX = pageW / 2 - 10;

    // Text parts
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";

    doc.setFontSize(8);

    // Measure widths
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);

    doc.setFont("Inter-Bold", "bold");

    // Start X so whole string is right-aligned
    const startX = rightX;

    // Draw normal part
    doc.setFont("Inter-Regular", "normal");
    doc.text(normalText, startX, y, {
      align: "center",
    });

    // Draw bold part
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, startX + 1 + normalW, y, {
      align: "center",
    });
  }
  // setDownload(false);
  return doc;
};
const CommercialInvoice = ({
  globalData,
  terms,
  qrCodeDataUrl,  
  selectedTheme,
  invoiceConfig,
}) => {
  const {
    documentConfig,
    showTaxDetails,
    showBillDetails,
    isPreSale, 
    isInterState,   
    hasPayment
  } = invoiceConfig;
  const { storeSettings } = usePermission();
 return (
    <>
      {showTaxDetails ? (
        <>
          <div className="tm_container">
            <div className="tm_invoice_wrap">
              <div className="tm_invoice tm_style1 tm_type1">
                <div className="tm_invoice_in p-2">
                  <div className="d-flex justify-content-between mb-3">
                    <div
                      className="tm_text_uppercase"
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {documentConfig.title && <>{documentConfig.title}</>}
                    </div>
                    <div>ORIGINAL FOR RECIPIENT</div>
                  </div>
                  <div className="row">
                    <div className="col-7 pe-1 ">
                      <div className="d-flex gap-2 align-items-start">
                        {/* <div className="tm_logo">
                                      {data?.salefrom?.image && (
                                        <img
                                          src={
                                            uRLs.IMAGE_URL +
                                            "profile/" +
                                            data?.salefrom?.image
                                          }
                                          className="img-rounded logo mb-0"
                                          height="45"
                                          width="150"
                                          style={{ objectFit: "contain" }}
                                        />
                                      )}
                                    </div> */}
                        <div>
                          <div
                            style={{
                              fontSize: "17px",
                              fontWeight: "600",
                              marginBottom: "5px",
                            }}
                          >
                            {documentConfig?.fromData?.title || ""}
                          </div>
                          {documentConfig?.fromData?.gstnumber && (
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                marginBottom: "0px",
                              }}
                            >
                              <>GSTIN:</>{" "}
                              {documentConfig?.fromData?.gstnumber || ""}
                            </div>
                          )}
                          <div>
                            {documentConfig?.fromData?.addressline1 || ""}{" "}
                            {documentConfig?.fromData?.addressline2 || ""}{" "}
                          </div>
                          <div>
                            {documentConfig?.fromData?.district || ""}
                            {", "}
                            {documentConfig?.fromData?.state || ""}
                            {", "}
                            {documentConfig?.fromData?.pincode || ""}
                          </div>
                          {/* <div>
                                        CIN :{data?.salefrom?.cinnumber}
                                      </div> */}
                          {/* {data?.salefrom?.pannumber && (
                                        <div>
                                          <strong>PAN:</strong>{" "}
                                          {data?.salefrom?.pannumber || ""}
                                        </div>
                                      )} */}
                          <div>
                            {" "}
                            Mobile: {documentConfig?.fromData?.phone || ""}
                            {documentConfig?.fromData?.additionalPhone && (
                              <>, {documentConfig.fromData.additionalPhone}</>
                            )}
                          </div>
                          {documentConfig?.fromData?.pannumber && (
                            <div>
                              PAN: {documentConfig?.fromData?.pannumber || ""}
                            </div>
                          )}
                          {documentConfig?.fromData?.fssai && (
                            <div>
                              FSSAI: {documentConfig?.fromData?.fssai || ""}
                            </div>
                          )}
                          {documentConfig?.fromData?.cinnumber && (
                            <div>
                              CIN: {documentConfig?.fromData?.cinnumber || ""}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-5 ps-1 ">
                      <div className="tm_logo text-end">
                        {documentConfig?.image && (
                          <img
                            src={                              
                              documentConfig?.image
                            }
                            className="img-rounded logo mb-0"
                            height="45"
                            width="150"
                            style={{ objectFit: "contain" }}
                          />
                        )}
                      </div>
                      {/* <div className="border h-100 p-2">
                                  <div className="tm_text_uppercase text-end">
                                    {data.salemode &&
                                      formatTrackMode(data.salemode)}
                                  </div>
                                  <div>
                                    <b>Invoice #</b> : {data.saleno}
                                  </div>
                                  <div>
                                    <b>Date</b> : {saleTime.date}
                                  </div>
                                  <div>
                                    <b>Place Of Supply</b> :{" "}
                                    {data?.salefrom?.state || ""}
                                  </div>
                                  <div>
                                    <b>Due Date</b> : {saleTime.date}
                                  </div>
                                  <div>
                                    <b>Sales Person</b> : -
                                  </div>
                                </div> */}
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-4 pe-1">
                      <div>
                        <div
                          style={{
                            marginBottom: "4px",
                          }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            {documentConfig?.numberLabel}
                          </span>
                          {""}
                          <strong className="font-14">
                            {documentConfig?.number}
                          </strong>
                        </div>
                        {documentConfig?.toData && (
                            <>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // fontWeight: "600",
                                  marginBottom: "5px",
                                }}
                              >
                                {documentConfig?.toLabel}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  marginBottom: "0px",
                                }}
                              >
                                <strong>
                                  {documentConfig?.toData?.name || ""}
                                </strong>
                              </div>
                              <div>
                                {" "}
                                ph: {documentConfig?.toData?.phone || ""}
                              </div>
                              {documentConfig?.toData?.referenceid && (
                                <div>
                                  Reference No:{" "}
                                  {documentConfig?.toData?.referenceid || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.gstnumber && (
                                <div>
                                  <strong>GSTIN:</strong>{" "}
                                  {documentConfig?.toData?.gstnumber || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.fssai && (
                                <div>
                                  <strong>FASSI:</strong>{" "}
                                  {documentConfig?.toData?.fssai || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.pannumber && (
                                <div>
                                  <strong>PAN:</strong>{" "}
                                  {documentConfig?.toData?.pannumber || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.cinnumber && (
                                <div>
                                  <strong>CIN :</strong>
                                  {documentConfig?.toData?.cinnumber || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.state && (
                                <div className="my-2 font-14">
                                  Place Of Supply :
                                  <strong>
                                    {documentConfig?.toData?.state || ""}{" "}
                                  </strong>
                                </div>
                              )}{" "}
                            </>
                          )}
                      </div>
                    </div>
                    <div className="col-4 ps-1">
                      <div>
                        <div
                          style={{
                            marginBottom: "4px",
                          }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            {documentConfig?.dateLabel || "Date :"}
                          </span>
                          {""}
                          <strong className="font-14">
                            {documentConfig?.Date?.date}
                          </strong>
                        </div>
                        {documentConfig?.toData && (
                            <>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // fontWeight: "600",
                                  marginBottom: "5px",
                                }}
                              >
                                Billing Address
                              </div>

                              <div>
                                {documentConfig?.toData?.addressline1 || ""}{" "}
                                {documentConfig?.toData?.addressline2 ||
                                  ""}{" "}
                              </div>
                              <div>
                                {documentConfig?.toData?.state || ""}{" "}
                                {documentConfig?.toData?.district || ""}{" "}
                                {documentConfig?.toData?.country || ""} -{" "}
                                {documentConfig?.toData?.pincode || ""}
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                    <div className="col-4 ps-1">
                      <div>
                        <div
                          style={{
                            marginBottom: "4px",
                          }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            {documentConfig?.timeLabel || "Time :"}
                          </span>
                          {""}
                          <strong className="font-14">
                            {documentConfig?.Date?.time}
                          </strong>
                        </div>
                        {documentConfig?.toData &&
                          documentConfig?.toData?.shippingAddress
                            ?.addressline1 &&
                          documentConfig?.toData?.shippingAddress
                            ?.addressline2 && (
                            <>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // fontWeight: "600",
                                  marginBottom: "5px",
                                }}
                              >
                                Shipping Address
                              </div>
                              {/* <div
                                      style={{
                                        fontSize: "12px",
                                        marginBottom: "0px",
                                      }}
                                    >
                                      {data.saleto?.name || ""}
                                    </div> */}
                              <div>
                                {documentConfig?.toData?.shippingAddress
                                  ?.addressline1 || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.addressline2 || ""}{" "}
                              </div>
                              <div>
                                {documentConfig?.toData?.shippingAddress
                                  ?.state || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.district || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.country || ""}{" "}
                                -{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.pincode || ""}
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                  </div>

                  <table
                    // className="items"
                    width="100%"
                    style={{
                      marginTop: "6px",
                      borderCollapse: "collapse",
                      borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                      // borderBottom: "1px solid #000"
                    }}
                    cellPadding="4"
                  >
                    <thead>
                      <tr
                        style={{
                          borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                      >
                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "20px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          #
                        </th>
                        <th
                          className="force-style p-1 px-2"
                          style={{
                            // width: "240px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Item
                        </th>

                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "50px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          MRP
                        </th>
                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            //     ?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Selling Price
                        </th>
                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Qty
                        </th>

                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentConfig?.items?.map((product, index) => (
                        <tr
                          key={index}
                          style={{
                            borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                            borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "left",
                              fontSize: "12px",
                              width: "53%",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            <div>{product.title}</div>
                            {/* {product.imode === "SERVICE" && (
                                      <div className="ms-0 fsz-10 lh-13 mb-1">
                                        {product.description}
                                      </div>
                                    )} */}
                            {/* {product.imode === "PRODUCT" &&
                                        product.note && (
                                          <div className="ms-0 fsz-10 mb-1">
                                            {product?.note}
                                          </div>
                                        )} */}
                            {product.hsnsac && (
                              <div>
                                <span className="fw-medium">
                                  {" "}
                                  {product.imode === "SERVICE"
                                    ? "SAC:"
                                    : "HSN:"}
                                </span>{" "}
                                {product.hsnsac}
                              </div>
                            )}
                            {/* {product.discount > 0 && (
                                      <>
                                        <div>
                                          <span className="fw-medium">MRP : </span> 
                                          {CurrencyConverter.toCurrency(
                                            product.mrp || 0,
                                            "INR"
                                          )}
                                        </div>
                                        <div>
                                          <span className="fw-medium">Discount : </span> -
                                          
                                          {CurrencyConverter.toCurrency(
                                            product.discount || 0,
                                            "INR"
                                          )}
                                        </div>
                                      </>
                                    )} */}
                          </td>
                          {/* <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      {product.hsnsac}
                                    </td> */}
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.mrp}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.baseprice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.quantity}
                            <span className="font-11 ms-1">
                              {(() => {
                                const unitCode = product?.isSecondaryUnit
                                  ? product?.unitConfig?.secondaryUnitCode
                                  : product?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </span>
                          </td>
                          {/* <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      {CurrencyConverter.toCurrency(
                                        product?.lineTotalBasePrice || 0,
                                        "INR",
                                      )}
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      {`${product?.taxpercentage}%`}
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      
                                      {CurrencyConverter.toCurrency(
                                        product?.lineTotalTaxAmount || 0,
                                        "INR",
                                      )}
                                    </td> */}

                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.lineGrandTotal}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="3"></td>
                        <td
                          colSpan="2"
                          style={{
                            borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "15px",
                              fontWeight: 600,
                              textAlign: "start",
                            }}
                          >
                            Total
                          </div>
                        </td>
                        <td
                          style={{
                            textAlign: "end",
                            fontSize: "14px",
                            fontWeight: 600,
                            borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {documentConfig?.grandTotal}{" "}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3"></td>
                        <td colSpan="2" className="text-end">
                          Total Amount Before Tax{" "}
                        </td>
                        <td
                          style={{
                            textAlign: "end",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {documentConfig?.totalBasePrice}{" "}
                        </td>
                      </tr>
                      {isInterState ? (
                        <tr>
                          <td colSpan="3"></td>
                          <td colSpan="2" className="text-end">
                            IGST
                          </td>
                          <td
                            style={{
                              textAlign: "end",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {documentConfig?.igstAmount}{" "}
                          </td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <td colSpan="3"></td>
                            <td colSpan="2" className="text-end">
                              CGST
                            </td>
                            <td
                              style={{
                                textAlign: "end",
                                fontSize: "12px",
                                fontWeight: 600,
                                padding: "2px 4px",
                              }}
                            >
                              {documentConfig?.cgstAmount}{" "}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="2"></td>
                            <td colSpan="3" className="text-end">
                              SGST
                            </td>
                            <td
                              style={{
                                textAlign: "end",
                                fontSize: "12px",
                                fontWeight: 600,
                                padding: "2px 4px",
                              }}
                            >
                              {documentConfig?.sgstAmount}{" "}
                            </td>
                          </tr>
                        </>
                      )}
                      <tr
                        style={{
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        <td colSpan={2} style={{ width: "30%" }}>
                          Total Items : {documentConfig?.items?.length}
                        </td>

                        <td
                          colSpan={4}
                          style={{
                            textAlign: "end",
                            fontSize: "12px",
                            fontWeight: 500,
                            marginBottom: "0",
                          }}
                        >
                          Total Amount (in Words)
                          {documentConfig?.amountInWords}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3"></td>
                        <td colSpan="2" className="text-end fw-bold">
                          Amount Payable
                        </td>
                        <td className="fw-bold text-end font-12 fw-600 mb-0">
                          {documentConfig?.balanceDue}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3"></td>
                        <td colSpan="2" className="text-end">
                          Amount Paid
                        </td>
                        <td className="text-end font-12">
                          { documentConfig?.paidAmount}{" "}
                        </td>
                      </tr>
                      {hasPayment && (
                        <>
                          {parseAmount(documentConfig?.cashPaid) > 0 && (
                            <tr>
                              <td colSpan="2"></td>
                              <td
                                colSpan={4}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b>{documentConfig?.cashPaid}</b> Paid Via Cash
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.upiPaid) > 0 && (
                            <tr>
                              <td colSpan="2"></td>
                              <td
                                colSpan={4}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b>{documentConfig?.upiPaid}</b> Paid Via UPI
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.cardPaid) > 0 && (
                            <tr>
                              <td colSpan="2"></td>
                              <td
                                colSpan={4}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b>{documentConfig?.cardPaid} </b>
                                Paid Via Card
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.onlinePaid) > 0 && (
                            <tr>
                              <td colSpan="2"></td>
                              <td
                                colSpan={4}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b> {documentConfig?.onlinePaid}</b> Paid Via
                                Online
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                  <div className="row mt-4 align-items-start">
                    {/* 🔹 LEFT: QR Code */}
                    <div className="col-2 px-0">
                      {qrCodeDataUrl && (
                        <>
                          <span className="px-2 fw-bold pb-0">
                            Pay Using UPI
                          </span>
                            <img
                              src={qrCodeDataUrl}
                              alt="QR Code"
                              style={{
                                width: "90px",
                                height: "90px",
                              }}
                            />                        
                        </>
                      )}
                    </div>

                    {/* 🔹 CENTER: Bank Details */}
                    <div className="col-6 px-0">
                      {documentConfig?.bankDetails && (
                       <>
                            <strong>Bank Details:</strong>                            
                              {documentConfig?.bankDetails?.accountNo && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Account #:</span>
                                  {documentConfig.bankDetails.accountNo}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.ifsc && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>IFSC:</span>
                                  {documentConfig.bankDetails.ifsc}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.beneficiaryName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Name:</span>
                                  {documentConfig.bankDetails.beneficiaryName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.bankName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Bank Name:</span>
                                  {documentConfig.bankDetails.bankName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.branch && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Branch Name:</span>
                                  {documentConfig.bankDetails.branch}
                                </div>
                              )}

                              {documentConfig?.upiId && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>UPI ID:</span>
                                  {documentConfig?.upiId}
                                </div>
                              )}
                          </>
                      )}
                    </div>

                    {/* 🔹 RIGHT: Signature */}
                    <div className="col-4 text-end">
                      {storeSettings?.invoiceSettings?.signature && (
                        <img
                          src={
                            uRLs.IMAGE_URL +
                            storeSettings?.invoiceSettings?.signature
                          }
                          alt="Signature"
                          style={{
                            width: "120px",
                            marginBottom: "5px",
                          }}
                        />
                      )}

                      <div style={{ fontSize: "12px" }}>
                        Issued By, Signature
                      </div>
                    </div>
                  </div>
                  {documentConfig?.remarks && (
                    <table width="100%" className="mb-2 mt-1">
                      <tbody>
                        <tr>
                          <td width="100%">
                            <div className="font-12 fw-600 align-left text-black mb-1">
                              Remark
                            </div>

                            <div
                              className="font-12 p-0"
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                minHeight: "30px",
                              }}
                            >
                              {capitalizeFirstLetter(documentConfig?.remarks)}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  {terms?.length > 0 && !isPreSale && (
                    <table width="100%" className="mt-2">
                      <tbody>
                        <tr>
                          <td width="100%">
                            <div className="font-12 text-black text-start fw-600">
                              Terms & Conditions
                            </div>
                            <div className="terms-box font-11">{terms}</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  {storeSettings?.negobillBranding && (
                    <div className="d-flex justify-content-center">
                      {" "}
                      <span>
                        Powered by <strong>NegoBill.com</strong>
                      </span>
                    </div>
                  )}

                  {/* <table
                            width="100%"
                            style={{ borderCollapse: "collapse" }}
                            cellPadding="8"
                          >
                            <tbody>
                            <tr>
                            {storeSettings?.invoiceSettings?.signature && (
                                  <td className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings?.signature
                                      }
                                      width={150}
                                    />
                                  </td> )}
          
                              </tr>
                              <tr>
                                <td className="text-end p-0">Issued By,Signature</td>
                              </tr>
                              <tr>
                                <td className="text-end p-0 fw-bold ">
                                  {settings?.saleProfileData?.title}
                                </td>
                              </tr>
                            </tbody>
                          </table> */}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : showBillDetails ? (
        <>
          <div className="tm_container">
            <div className="tm_invoice_wrap">
              <div className="tm_invoice tm_style1 tm_type1">
                <div className="tm_invoice_in p-2">
                  <div className="d-flex justify-content-between mb-3">
                    <div
                      className="tm_text_uppercase"
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {documentConfig.title && <>{documentConfig.title}</>}
                    </div>
                    <div>ORIGINAL FOR RECIPIENT</div>
                  </div>
                  <div className="row">
                    <div className="col-7 pe-1 ">
                      <div className="d-flex gap-2 align-items-start">
                        {/* <div className="tm_logo">
                                      {data?.salefrom?.image && (
                                        <img
                                          src={
                                            uRLs.IMAGE_URL +
                                            "profile/" +
                                            data?.salefrom?.image
                                          }
                                          className="img-rounded logo mb-0"
                                          height="45"
                                          width="150"
                                          style={{ objectFit: "contain" }}
                                        />
                                      )}
                                    </div> */}
                        <div>
                          <div
                            style={{
                              fontSize: "17px",
                              fontWeight: "600",
                              marginBottom: "5px",
                            }}
                          >
                            {documentConfig?.fromData?.title || ""}
                          </div>
                          {/* {data?.salefrom?.gstnumber && (
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          marginBottom: "0px",
                                        }}
                                      >
                                         <>GSTIN:</>{" "}
                                          {data?.salefrom?.gstnumber || ""}
                                      </div>)} */}
                          <div>
                            {documentConfig?.fromData?.addressline1 || ""}{" "}
                            {documentConfig?.fromData?.addressline2 || ""}{" "}
                          </div>
                          <div>
                            {documentConfig?.fromData?.district || ""}
                            {", "}
                            {documentConfig?.fromData?.state || ""}
                            {", "}
                            {documentConfig?.fromData?.pincode || ""}
                          </div>
                          {/* <div>
                                        CIN :{data?.salefrom?.cinnumber}
                                      </div> */}
                          {/* {data?.salefrom?.pannumber && (
                                        <div>
                                          <strong>PAN:</strong>{" "}
                                          {data?.salefrom?.pannumber || ""}
                                        </div>
                                      )} */}
                          <div>
                            {" "}
                            Mobile: {documentConfig?.fromData?.phone || ""}
                            {documentConfig?.fromData?.additionalPhone && (
                              <>, {documentConfig.fromData.additionalPhone}</>
                            )}
                          </div>
                          {documentConfig?.fromData?.pannumber && (
                            <div>
                              PAN: {documentConfig?.fromData?.pannumber || ""}
                            </div>
                          )}
                          {documentConfig?.fromData?.fssai && (
                            <div>
                              FSSAI: {documentConfig?.fromData?.fssai || ""}
                            </div>
                          )}
                          {documentConfig?.fromData?.cinnumber && (
                            <div>
                              CIN: {documentConfig?.fromData?.cinnumber || ""}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-5 ps-1 ">
                      <div className="tm_logo text-end">
                        {documentConfig?.image && (
                          <img
                            src={
                              documentConfig?.image
                            }
                            className="img-rounded logo mb-0"
                            height="45"
                            width="150"
                            style={{ objectFit: "contain" }}
                          />
                        )}
                      </div>
                      {/* <div className="border h-100 p-2">
                                  <div className="tm_text_uppercase text-end">
                                    {data.salemode &&
                                      formatTrackMode(data.salemode)}
                                  </div>
                                  <div>
                                    <b>Invoice #</b> : {data.saleno}
                                  </div>
                                  <div>
                                    <b>Date</b> : {saleTime.date}
                                  </div>
                                  <div>
                                    <b>Place Of Supply</b> :{" "}
                                    {data?.salefrom?.state || ""}
                                  </div>
                                  <div>
                                    <b>Due Date</b> : {saleTime.date}
                                  </div>
                                  <div>
                                    <b>Sales Person</b> : -
                                  </div>
                                </div> */}
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-4 pe-1">
                      <div>
                        <div
                          style={{
                            marginBottom: "4px",
                          }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            {" "}
                            {documentConfig?.numberLabel}
                          </span>
                          {""}
                          <strong className="font-14">
                            {documentConfig?.number}
                          </strong>
                        </div>
                        {documentConfig?.toData && (
                            <>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // fontWeight: "600",
                                  marginBottom: "5px",
                                }}
                              >
                                {documentConfig?.fromLabel}
                              </div>

                              <div
                                style={{
                                  fontSize: "12px",
                                  marginBottom: "0px",
                                }}
                              >
                                <strong>
                                  {documentConfig?.toData?.name || ""}
                                </strong>
                              </div>

                              <div>
                                {" "}
                                ph: {documentConfig?.toData?.phone || ""}
                              </div>
                              {documentConfig?.toData?.referenceid && (
                                <div>
                                  Reference No:{" "}
                                  {documentConfig?.toData?.referenceid || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.gstnumber && (
                                <div>
                                  <strong>GSTIN:</strong>{" "}
                                  {documentConfig?.toData?.gstnumber || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.fssai && (
                                <div>
                                  <strong>FASSI:</strong>{" "}
                                  {documentConfig?.toData?.fssai || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.pannumber && (
                                <div>
                                  <strong>PAN:</strong>{" "}
                                  {documentConfig?.toData?.pannumber || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.cinnumber && (
                                <div>
                                  <strong>CIN :</strong>
                                  {documentConfig?.toData?.cinnumber || ""}
                                </div>
                              )}
                              {documentConfig?.toData?.state && (
                                <div className="my-2 font-14">
                                  Place Of Supply :
                                  <strong>
                                    {documentConfig?.toData?.state || ""}{" "}
                                  </strong>
                                </div>
                              )}
                            </>
                          )}
                      </div>
                    </div>
                    <div className="col-4 ps-1">
                      <div>
                        <div
                          style={{
                            marginBottom: "4px",
                          }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            {documentConfig?.dateLabel || "Date :"}
                          </span>
                          {""}
                          <strong className="font-14">
                            {documentConfig?.Date?.date}
                          </strong>
                        </div>
                        {documentConfig?.toData && (
                            <>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // fontWeight: "600",
                                  marginBottom: "5px",
                                }}
                              >
                                Billing Address
                              </div>

                              <div>
                                {documentConfig?.toData?.addressline1 || ""}{" "}
                                {documentConfig?.toData?.addressline2 ||
                                  ""}{" "}
                              </div>
                              <div>
                                {documentConfig?.toData?.state || ""}{" "}
                                {documentConfig?.toData?.district || ""}{" "}
                                {documentConfig?.toData?.country || ""} -{" "}
                                {documentConfig?.toData?.pincode || ""}
                              </div>
                            </>
                          )}
                      </div>
                    </div>

                    <div className="col-4 ps-1">
                      <div>
                        <div
                          style={{
                            marginBottom: "4px",
                          }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            {documentConfig?.timeLabel || "Time :"}
                          </span>
                          {""}
                          <strong className="font-14">
                            {documentConfig?.Date?.time}
                          </strong>
                        </div>
                        {documentConfig?.toData &&
                          documentConfig?.toData?.shippingAddress
                            ?.addressline1 &&
                          documentConfig?.toData?.shippingAddress
                            ?.addressline2 && (
                            <>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // fontWeight: "600",
                                  marginBottom: "5px",
                                }}
                              >
                                Shipping Address
                              </div>
                              {/* <div
                                      style={{
                                        fontSize: "12px",
                                        marginBottom: "0px",
                                      }}
                                    >
                                      {data.saleto?.name || ""}
                                    </div> */}
                              <div>
                                {documentConfig?.toData?.shippingAddress
                                  ?.addressline1 || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.addressline2 || ""}{" "}
                              </div>
                              <div>
                                {documentConfig?.toData?.shippingAddress
                                  ?.state || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.district || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.country || ""}{" "}
                                -{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.pincode || ""}
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                  </div>

                  <table
                    // className="items"
                    width="100%"
                    style={{
                      marginTop: "6px",
                      borderCollapse: "collapse",
                      borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                      // borderBottom: "1px solid #000"
                    }}
                    cellPadding="4"
                  >
                    <thead>
                      <tr
                        className="p-2"
                        style={{
                          borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                      >
                        <th
                          className="force-style p-1 px-2 "
                          style={{
                            width: "20px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          #
                        </th>
                        <th
                          className="force-style p-1 px-2"
                          style={{
                            // width: "240px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Item
                        </th>

                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "50px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          MRP
                        </th>
                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            //     ?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Selling Price
                        </th>
                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Qty
                        </th>

                        <th
                          className="force-style p-1 px-2"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentConfig?.items?.map((product, index) => (
                        <tr
                          key={index}
                          style={{
                            borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                            borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "left",
                              fontSize: "12px",
                              width: "53%",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            <div>{product.title}</div>
                            {/* {product.imode === "SERVICE" && (
                                      <div className="ms-0 fsz-10 lh-13 mb-1">
                                        {product.description}
                                      </div>
                                    )} */}
                            {/* {product.imode === "PRODUCT" &&
                                        product.note && (
                                          <div className="ms-0 fsz-10 mb-1">
                                            {product?.note}
                                          </div>
                                        )} */}
                            {product.hsnsac && (
                              <div>
                                <span className="fw-medium">
                                  {" "}
                                  {product.imode === "SERVICE"
                                    ? "SAC:"
                                    : "HSN:"}
                                </span>{" "}
                                {product.hsnsac}
                              </div>
                            )}
                            {/* {product.discount > 0 && (
                                      <>
                                        <div>
                                          <span className="fw-medium">MRP : </span> 
                                          {CurrencyConverter.toCurrency(
                                            product.mrp || 0,
                                            "INR"
                                          )}
                                        </div>
                                        <div>
                                          <span className="fw-medium">Discount : </span> -
                                          
                                          {CurrencyConverter.toCurrency(
                                            product.discount || 0,
                                            "INR"
                                          )}
                                        </div>
                                      </>
                                    )} */}
                          </td>
                          {/* <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      {product.hsnsac}
                                    </td> */}
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.mrp}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.baseprice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.quantity}
                            <span className="font-11 ms-1">
                              {(() => {
                                const unitCode = product?.isSecondaryUnit
                                  ? product?.unitConfig?.secondaryUnitCode
                                  : product?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </span>
                          </td>
                          {/* <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      {CurrencyConverter.toCurrency(
                                        product?.lineTotalBasePrice || 0,
                                        "INR",
                                      )}
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      {`${product?.taxpercentage}%`}
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      
                                      {CurrencyConverter.toCurrency(
                                        product?.lineTotalTaxAmount || 0,
                                        "INR",
                                      )}
                                    </td> */}

                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              // "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.lineGrandTotal}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="3"></td>
                        <td
                          colSpan="2"
                          style={{
                            borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "15px",
                              fontWeight: 600,
                              textAlign: "start",
                            }}
                          >
                            Total
                          </div>
                        </td>
                        <td
                          style={{
                            textAlign: "end",
                            fontSize: "14px",
                            fontWeight: 600,
                            borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {documentConfig?.grandTotal}{" "}
                        </td>
                      </tr>

                      <tr
                        style={{
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        <td colSpan={2} style={{ width: "30%" }}>
                          Total Items : {documentConfig?.items?.length}
                        </td>

                        <td
                          colSpan={4}
                          style={{
                            textAlign: "end",
                            fontSize: "12px",
                            fontWeight: 500,
                            marginBottom: "0",
                          }}
                        >
                          Total Amount (In Words):
                          {documentConfig?.amountInWords}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3"></td>
                        <td colSpan="2" className="text-end  fw-bold">
                          Amount Payable
                        </td>
                        <td className="fw-bold text-end font-12 fw-600 mb-0">
                          {documentConfig?.balanceDue}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3"></td>
                        <td colSpan="2" className="text-end">
                          Amount Paid
                        </td>
                        <td className="text-end font-12">
                          {documentConfig?.paidAmount}{" "}
                        </td>
                      </tr>
                      {hasPayment && (
                        <>
                          {parseAmount(documentConfig?.cashPaid) > 0 && (
                            <tr>
                              <td colSpan="3"></td>
                              <td
                                colSpan={3}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b>{documentConfig?.cashPaid}</b> Paid Via Cash
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.upiPaid) > 0 && (
                            <tr>
                              <td colSpan="3"></td>
                              <td
                                colSpan={3}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b>{documentConfig?.upiPaid}</b> Paid Via UPI
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.cardPaid) > 0 && (
                            <tr>
                              <td colSpan="3"></td>
                              <td
                                colSpan={3}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b>{documentConfig?.cardPaid} </b>
                                Paid Via Card
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.onlinePaid) > 0 && (
                            <tr>
                              <td colSpan="3"></td>
                              <td
                                colSpan={3}
                                className="text-end font-12 mb-0 pb-0"
                              >
                                <b> {documentConfig?.onlinePaid}</b> Paid Via
                                Online
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                  <div className="row mt-4 align-items-start">
                    {/* 🔹 LEFT: QR Code */}
                    <div className="col-2 px-0">
                      {qrCodeDataUrl && (
                        <>
                          <span className="px-2 fw-bold pb-0">
                            Pay Using UPI
                          </span>                         
                            <img
                              src={qrCodeDataUrl}
                              alt="QR Code"
                              style={{
                                width: "90px",
                                height: "90px",
                              }}
                            />
                          
                        </>
                      )}
                    </div>

                    {/* 🔹 CENTER: Bank Details */}
                    <div className="col-6 px-0">
                      {documentConfig?.bankDetails && (
                         <>
                            <strong>Bank Details:</strong>                            
                              {documentConfig?.bankDetails?.accountNo && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Account #:</span>
                                  {documentConfig.bankDetails.accountNo}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.ifsc && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>IFSC:</span>
                                  {documentConfig.bankDetails.ifsc}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.beneficiaryName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Name:</span>
                                  {documentConfig.bankDetails.beneficiaryName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.bankName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Bank Name:</span>
                                  {documentConfig.bankDetails.bankName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.branch && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>Branch Name:</span>
                                  {documentConfig.bankDetails.branch}
                                </div>
                              )}

                              {documentConfig?.upiId && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "100px" }}>UPI ID:</span>
                                  {documentConfig?.upiId}
                                </div>
                              )}
                          </>
                      )}
                    </div>

                    {/* 🔹 RIGHT: Signature */}
                    <div className="col-4 text-end">
                      {storeSettings?.invoiceSettings?.signature && (
                        <img
                          src={
                            uRLs.IMAGE_URL +
                            storeSettings?.invoiceSettings?.signature
                          }
                          alt="Signature"
                          style={{
                            width: "120px",
                            marginBottom: "5px",
                          }}
                        />
                      )}

                      <div style={{ fontSize: "12px" }}>
                        Issued By, Signature
                      </div>
                    </div>
                  </div>
                  {documentConfig?.remarks && (
                    <table width="100%" className="mb-2 mt-1">
                      <tbody>
                        <tr>
                          <td width="100%">
                            <div className="font-12 fw-600 align-left text-black mb-1">
                              Remark
                            </div>

                            <div
                              className="font-12 p-0"
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                minHeight: "30px",
                              }}
                            >
                              {capitalizeFirstLetter(documentConfig?.remarks)}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  <table width="100%">
                    <tbody>
                      {terms?.length > 0 && !isPreSale && (
                        <tr>
                          <td width="100%">
                            <div className="font-12 text-black text-start fw-600">
                              Terms & Conditions
                            </div>
                            <div className="terms-box font-11">{terms}</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {storeSettings?.negobillBranding && (
                    <div className="d-flex justify-content-center">
                      {" "}
                      <span>
                        Powered by <strong>NegoBill.com</strong>
                      </span>
                    </div>
                  )}

                  {/* <table
                            width="100%"
                            style={{ borderCollapse: "collapse" }}
                            cellPadding="8"
                          >
                            <tbody>
                            <tr>
                            {storeSettings?.invoiceSettings?.signature && (
                                  <td className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings?.signature
                                      }
                                      width={150}
                                    />
                                  </td> )}
          
                              </tr>
                              <tr>
                                <td className="text-end p-0">Issued By,Signature</td>
                              </tr>
                              <tr>
                                <td className="text-end p-0 fw-bold ">
                                  {settings?.saleProfileData?.title}
                                </td>
                              </tr>
                            </tbody>
                          </table> */}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="default-content">
          <p>No content available for this sale mode.</p>
        </div>
      )}
    </>
  );
};

export default CommercialInvoice;
